(() => {
  if (window.__deliveryCountdownInit) return;
  window.__deliveryCountdownInit = true;

  const TIMEZONE = 'Europe/London';

  const getLondonParts = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    const map = {};
    for (const part of parts) {
      if (part.type !== 'literal') map[part.type] = part.value;
    }

    return {
      year: Number(map.year),
      month: Number(map.month),
      day: Number(map.day),
      hour: Number(map.hour),
      minute: Number(map.minute)
    };
  };

  const toDateKey = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const parseExcludedDates = (value) => {
    if (!value) return new Set();

    const dates = new Set();

    value.split(',').forEach((item) => {
      const trimmed = item.trim();
      if (!trimmed) return;

      // Preferred UK format: dd-mm-yyyy (also accepts d-m-yyyy)
      let match = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          dates.add(toDateKey(year, month, day));
        }
        return;
      }

      // Legacy ISO format: yyyy-mm-dd
      match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        dates.add(toDateKey(Number(match[1]), Number(match[2]), Number(match[3])));
      }
    });

    return dates;
  };

  const parseCutoff = (value) => {
    const match = String(value || '15:00').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return { hour: 15, minute: 0 };

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return { hour: 15, minute: 0 };

    return { hour, minute };
  };

  const addCalendarDays = (year, month, day, amount) => {
    const date = new Date(Date.UTC(year, month - 1, day + amount));
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    };
  };

  const isWeekend = (year, month, day) => {
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return weekday === 0 || weekday === 6;
  };

  const isNonWorkingDay = (year, month, day, excluded) => {
    if (isWeekend(year, month, day)) return true;
    return excluded.has(toDateKey(year, month, day));
  };

  const nextWorkingDay = (year, month, day, excluded) => {
    let current = { year, month, day };
    while (isNonWorkingDay(current.year, current.month, current.day, excluded)) {
      current = addCalendarDays(current.year, current.month, current.day, 1);
    }
    return current;
  };

  const addWorkingDays = (year, month, day, days, excluded) => {
    let current = nextWorkingDay(year, month, day, excluded);
    let remaining = Math.max(1, days);

    while (remaining > 0) {
      current = addCalendarDays(current.year, current.month, current.day, 1);
      if (!isNonWorkingDay(current.year, current.month, current.day, excluded)) {
        remaining -= 1;
      }
    }

    return current;
  };

  const ordinal = (day) => {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${day}th`;

    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  };

  const formatDeliveryMessage = (year, month, day) => {
    const date = new Date(Date.UTC(year, month - 1, day));
    const weekday = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      timeZone: 'UTC'
    }).format(date);
    const monthName = new Intl.DateTimeFormat('en-GB', {
      month: 'long',
      timeZone: 'UTC'
    }).format(date);

    return `Delivery by ${weekday}, ${ordinal(day)} ${monthName}`;
  };

  const calculateDeliveryDate = (deliveryDays, cutoffValue, excludedValue) => {
    const now = getLondonParts();
    const cutoff = parseCutoff(cutoffValue);
    const excluded = parseExcludedDates(excludedValue);
    const days = Math.max(1, Number.parseInt(deliveryDays, 10) || 1);

    let start = { year: now.year, month: now.month, day: now.day };
    const pastCutoff =
      now.hour > cutoff.hour || (now.hour === cutoff.hour && now.minute >= cutoff.minute);

    if (pastCutoff) {
      start = addCalendarDays(start.year, start.month, start.day, 1);
    }

    start = nextWorkingDay(start.year, start.month, start.day, excluded);
    return addWorkingDays(start.year, start.month, start.day, days, excluded);
  };

  const renderCountdown = (el) => {
    if (!el || el.dataset.deliveryCountdownRendered === 'true') return;

    const deliveryDate = calculateDeliveryDate(
      el.dataset.deliveryDays,
      el.dataset.cutoff,
      el.dataset.excludedDates
    );

    el.textContent = formatDeliveryMessage(
      deliveryDate.year,
      deliveryDate.month,
      deliveryDate.day
    );
    el.dataset.deliveryCountdownRendered = 'true';
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-delivery-countdown]').forEach(renderCountdown);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
